<template>
  <div class="sibling-concession-report">
    <div class="no-print control-bar">
      <div class="d-flex align-items-center">
        <h4 class="mb-0">Sibling Concession Report</h4>
        <b-badge v-show="!loading" variant="light-success" class="ml-2">
          {{ totalFamilies }} Families · {{ totalStudents }} Students
        </b-badge>
      </div>
      <div class="action-buttons">
        <button class="btn-modern btn-print" @click="print">
          <feather-icon icon="PrinterIcon" size="18" class="mr-50" />
          <span>Print Report</span>
        </button>
        <button class="btn-modern btn-close" @click="close">
          <feather-icon icon="XIcon" size="18" class="mr-50" />
          <span>Close</span>
        </button>
      </div>
    </div>

    <div v-if="loading" class="text-center py-5">
      <b-spinner variant="primary" style="width: 3.5rem; height: 3.5rem" />
      <p class="mt-3 font-weight-bold">Loading sibling concession records...</p>
    </div>

    <div v-else-if="error" class="text-center py-5">
      <feather-icon icon="AlertCircleIcon" size="48" class="text-danger mb-3" />
      <h4 class="text-danger font-weight-bold">{{ error }}</h4>
      <b-button variant="primary" class="mt-3" @click="loadReportData">
        Retry
      </b-button>
    </div>

    <div v-else class="report-page">
      <div class="report-header-modern mb-4">
        <div class="d-flex align-items-center">
          <div v-if="schoolLogo" class="logo-wrapper mr-3">
            <img :src="schoolLogo" alt="Logo" class="school-logo-img" />
          </div>
          <div class="header-text-block">
            <h2 class="school-name-full mb-0">
              {{ campusName
              }}<span v-if="campusBranch" class="text-muted">
                - {{ campusBranch }}</span
              >
            </h2>
            <div class="report-info-line mt-1">
              <span class="report-name-text">Sibling Concession Report</span>
              <span class="date-range-text">
                <strong>Generated on {{ currentDateTime }}</strong>
              </span>
            </div>
            <div v-if="filterSummary" class="filter-info-line mt-1">
              {{ filterSummary }}
            </div>
          </div>
        </div>
      </div>

      <div v-if="!families.length" class="empty-state text-center py-5">
        <feather-icon icon="InboxIcon" size="32" class="text-muted mb-2" />
        <p class="mb-0">No sibling concession records found for the selected criteria.</p>
      </div>

      <div
        v-for="family in families"
        :key="`family-${family.sno}-${family.fatherCnic}`"
        class="family-block"
      >
        <div class="family-header">
          <div class="family-header-main">
            <strong class="family-father-name">{{
              family.fatherName || "—"
            }}</strong>
            <span class="family-meta">CNIC: {{ family.fatherCnic || "—" }}</span>
          </div>
          <div class="family-header-side">
            <span class="family-badge">{{ formatSiblingCount(family.siblingCount) }}</span>
            <span
              v-if="familyContact(family, 'father')"
              class="family-contact"
            >
              Father: {{ familyContact(family, "father") }}
            </span>
            <span
              v-if="familyContact(family, 'mother')"
              class="family-contact"
            >
              Mother: {{ familyContact(family, "mother") }}
            </span>
          </div>
        </div>

        <table class="ledger-style-table family-table">
          <thead>
            <tr>
              <th class="text-center" style="width: 4%">#</th>
              <th class="text-center" style="width: 7%">Roll</th>
              <th class="text-left" style="width: 16%">Student Name</th>
              <th class="text-center" style="width: 8%">Class</th>
              <th class="text-center" style="width: 9%">Admission Date</th>
              <th class="text-center" style="width: 12%">Category</th>
              <th class="text-center" style="width: 9%">Fee Status</th>
              <th class="text-right" style="width: 8%">Class Fee</th>
              <th class="text-right" style="width: 8%">Discount</th>
              <th class="text-right" style="width: 9%">Amount</th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="(student, index) in family.students || []"
              :key="`${family.sno}-${student.sID || index}`"
            >
              <td class="text-center font-weight-bold">
                {{ student.sno || index + 1 }}
              </td>
              <td class="text-center roll-cell">{{ student.roll || "—" }}</td>
              <td class="text-left font-weight-bold">
                {{ (student.name || "—").toUpperCase() }}
              </td>
              <td class="text-center">{{ student.className || "—" }}</td>
              <td class="text-center">
                {{ formatDate(student.admissionDate) }}
              </td>
              <td class="text-center">{{ student.category || "—" }}</td>
              <td class="text-center">{{ student.feeStatus || "—" }}</td>
              <td class="text-right">{{ formatAmount(student.classFee) }}</td>
              <td class="text-right">{{ formatAmount(student.discount) }}</td>
              <td class="text-right font-weight-bold">
                {{ formatAmount(student.amount) }}
              </td>
            </tr>
            <tr class="family-total-row">
              <td colspan="9" class="text-right font-weight-bold">
                Family Subtotal
              </td>
              <td class="text-right font-weight-bold">
                {{ formatAmount(familyTotalAmount(family)) }}
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <div v-if="families.length" class="grand-total-bar">
        <span>Grand Total</span>
        <strong>{{ formatAmount(grandTotalAmount) }}</strong>
      </div>
    </div>
  </div>
</template>

<script>
import { BButton, BSpinner, BBadge } from "bootstrap-vue";
import axios from "axios";

export default {
  name: "SiblingConcessionPrint",
  components: {
    BButton,
    BSpinner,
    BBadge,
  },
  data() {
    return {
      loading: false,
      error: null,
      families: [],
      totalFamilies: 0,
      totalStudents: 0,
      campusName: "MY SKOOL",
      campusBranch: "",
      schoolLogo: null,
      db: "",
      cID: 0,
      classID: 0,
      sectionID: 0,
      discountReferenceID: 0,
      discountRefName: "All",
      className: "",
      sectionName: "",
      reportDomain: (this.$store.state.report || "").replace(/_/g, ""),
    };
  },
  computed: {
    currentDateTime() {
      return new Date().toLocaleString("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
      });
    },
    grandTotalAmount() {
      return this.families.reduce(
        (sum, family) => sum + this.familyTotalAmount(family),
        0
      );
    },
    filterSummary() {
      const parts = [];
      if (this.className) parts.push(`Class: ${this.className}`);
      else if (this.classID && this.classID != 0)
        parts.push(`Class ID: ${this.classID}`);
      else parts.push("Class: All");

      if (this.sectionName) parts.push(`Section: ${this.sectionName}`);
      else if (this.sectionID && this.sectionID != 0)
        parts.push(`Section ID: ${this.sectionID}`);
      else parts.push("Section: All");

      parts.push(`Discount Reference: ${this.discountRefName || "All"}`);
      return parts.join(" | ");
    },
  },
  created() {
    this.initReport();
  },
  methods: {
    initReport() {
      if (this.reportDomain !== "myskoolaba") {
        this.error = "This report is only available for Myskool ABA.";
        return;
      }

      const query = this.$route.query;
      this.db = query.db || this.$store.state.userData.db;
      this.cID = Number(query.cID || this.$store.state.userData.cId || 0);
      this.classID = Number(query.classID || 0);
      this.sectionID = Number(query.sectionID || 0);
      this.discountReferenceID = Number(query.discountReferenceID || 0);
      this.discountRefName = query.discountRefName || "All";
      this.className = query.className || "";
      this.sectionName = query.sectionName || "";

      if (!this.db) {
        this.error =
          "Database identifier is missing. Please close and try again.";
        return;
      }

      this.loadCampusInfo();
      this.loadReportData();
    },
    async loadCampusInfo() {
      try {
        const response = await axios.get(
          "https://apiv26.myskool.app/api/campuses",
          {
            params: { db: this.db, cId: this.cID },
            headers: {
              Authorization: `Bearer ${this.$store.state.userData.token}`,
              accept: "application/json",
            },
          }
        );

        if (
          response.data &&
          response.data.status === "success" &&
          Array.isArray(response.data.data)
        ) {
          const match = response.data.data.find((c) => c.id == this.cID);
          if (match) {
            this.campusName = match.name;
            this.campusBranch = this.cID == 0 ? "All Campuses" : match.branch;
            this.schoolLogo = match.logo;
          }
        }
      } catch (e) {
        const branches = this.$store.state.branches || [];
        const currentBranch =
          branches.find((b) => b.id == this.cID) ||
          this.$store.state.currentBranch;
        if (currentBranch) {
          this.campusName =
            currentBranch.organizationName ||
            currentBranch.branch ||
            "MY SKOOL";
          this.campusBranch = currentBranch.branch || "";
        }
      }
    },
    async loadReportData() {
      this.loading = true;
      this.error = null;

      try {
        const url = `${this.$store.state.domain}Fees/SiblingsByFatherCnicReport`;
        const response = await axios.get(url, {
          params: {
            db: this.db,
            cID: this.cID,
            classID: this.classID || 0,
            sectionID: this.sectionID || 0,
            discountReferenceID: this.discountReferenceID || 0,
          },
          headers: {
            Authorization: `Bearer ${this.$store.state.userData.token}`,
            accept: "application/json",
          },
        });

        if (response.data && response.data.status === "success") {
          this.families = Array.isArray(response.data.data)
            ? response.data.data
            : [];
          this.totalFamilies =
            Number(response.data.totalFamilies) || this.families.length;
          this.totalStudents =
            Number(response.data.totalStudents) ||
            this.families.reduce(
              (sum, family) =>
                sum + (Array.isArray(family.students) ? family.students.length : 0),
              0
            );
        } else {
          this.error =
            response.data?.message ||
            "The server returned an unexpected response format.";
        }
      } catch (err) {
        console.error("Error fetching sibling concession report:", err);
        this.error =
          "Failed to retrieve report data. Please ensure the API endpoint is accessible.";
      } finally {
        this.loading = false;
      }
    },
    familyContact(family, type) {
      const students = Array.isArray(family?.students) ? family.students : [];
      const first = students[0];
      if (!first) return "";
      if (type === "father") return first.contact_father || "";
      return first.contact_mother || "";
    },
    familyTotalAmount(family) {
      const students = Array.isArray(family?.students) ? family.students : [];
      return students.reduce(
        (sum, student) => sum + (Number(student.amount) || 0),
        0
      );
    },
    formatDate(value) {
      if (!value) return "—";
      const date = new Date(value);
      if (Number.isNaN(date.getTime())) return "—";
      return date.toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      });
    },
    formatAmount(value) {
      const num = Number(value);
      if (Number.isNaN(num)) return "—";
      return num.toLocaleString("en-PK");
    },
    formatSiblingCount(count) {
      const total = Number(count) || 0;
      return `${total} ${total === 1 ? "Sibling" : "Siblings"}`;
    },
    print() {
      window.print();
    },
    close() {
      window.close();
    },
  },
};
</script>

<style lang="scss" scoped>
.sibling-concession-report {
  background: #f0f2f5;
  min-height: 100vh;
  padding: 2.5rem;
  font-family: "Outfit", "Inter", sans-serif;
  color: #2d3436;
}

.control-bar {
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(10px);
  padding: 1.25rem 2.5rem;
  margin-bottom: 2rem;
  border-radius: 12px;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.1);
  display: flex;
  justify-content: space-between;
  align-items: center;
  position: sticky;
  top: 1rem;
  z-index: 1000;
  border: 1px solid rgba(255, 255, 255, 0.3);

  h4 {
    color: #444;
    font-weight: 700;
    font-size: 1.25rem;
    letter-spacing: -0.5px;
  }
}

.action-buttons {
  display: flex;
  gap: 12px;
}

.btn-modern {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 10px 24px;
  border-radius: 10px;
  font-weight: 700;
  font-size: 0.95rem;
  cursor: pointer;
  transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1);
  border: none;
  outline: none;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);

  &:active {
    transform: scale(0.96);
  }
}

.btn-print {
  background: linear-gradient(135deg, #6c5ce7 0%, #a29bfe 100%);
  color: white;

  &:hover {
    background: linear-gradient(135deg, #5b4cc4 0%, #8c82eb 100%);
    box-shadow: 0 6px 20px rgba(108, 92, 231, 0.35);
    transform: translateY(-1px);
  }
}

.btn-close {
  background: white;
  color: #636e72;
  border: 1px solid #dfe6e9;

  &:hover {
    color: #e74c3c;
    border-color: #fab1a0;
    transform: translateY(-1px);
  }
}

.report-page {
  background: #ffffff;
  padding: 3rem 2.5rem;
  border-radius: 12px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.12);
  width: 98%;
  max-width: 1400px;
  margin: 30px auto;
  box-sizing: border-box;
}

.report-header-modern {
  border-bottom: 2px solid #333;
  padding-bottom: 20px;

  .school-logo-img {
    height: 130px;
    width: auto;
    object-fit: contain;
  }

  .school-name-full {
    font-size: 2rem;
    font-weight: 800;
    color: #000;
    text-transform: uppercase;
    letter-spacing: -0.5px;
  }

  .report-info-line {
    font-size: 1.1rem;
    color: #444;
    display: flex;
    align-items: center;
    flex-wrap: wrap;

    .report-name-text {
      font-weight: 700;
      border-right: 2px solid #ddd;
      padding-right: 15px;
      margin-right: 15px;
    }
  }

  .filter-info-line {
    font-size: 0.95rem;
    color: #636e72;
    font-weight: 600;
  }
}

.family-block {
  margin-bottom: 1.75rem;
  page-break-inside: avoid;
}

.family-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  flex-wrap: wrap;
  gap: 0.75rem;
  padding: 0.75rem 0.25rem 0.5rem;
  background: transparent;
  border: none;
  border-bottom: 1px solid #dee2e6;
}

.family-header-main {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.65rem;
}

.family-father-name {
  font-size: 1rem;
  color: #1f1635;
}

.family-meta {
  font-size: 0.88rem;
  color: #636e72;
  font-weight: 600;
}

.family-header-side {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem 1rem;
  justify-content: flex-end;
}

.family-badge,
.family-contact {
  font-size: 0.82rem;
  font-weight: 700;
  color: #4b5563;
}

.family-badge {
  color: #333;
}

.ledger-style-table {
  width: 100%;
  border-collapse: collapse;
  border: 1px solid #dee2e6;
  background: white;

  th,
  td {
    border: 1px solid #dee2e6;
    padding: 10px 8px;
    box-sizing: border-box;
    font-size: 0.88rem;
  }

  thead {
    background: #2c3e50 !important;

    th {
      color: white !important;
      font-weight: 800;
      text-transform: uppercase;
      font-size: 0.72rem;
      letter-spacing: 0.4px;
    }
  }

  tbody tr:nth-child(even):not(.family-total-row) {
    background-color: #f8f9fa;
  }

  .roll-cell {
    font-weight: 700;
    color: #333;
  }

  .family-total-row {
    background: #f8f9fa !important;
    border-top: 1px solid #dee2e6;
  }
}

.family-table {
  margin-top: 0.35rem;
}

.grand-total-bar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 1rem;
  padding: 1rem 1.25rem;
  border-radius: 10px;
  background: #2c3e50;
  color: #fff;
  font-size: 1rem;

  strong {
    font-size: 1.25rem;
  }
}

.empty-state {
  color: #636e72;
}

@media print {
  .no-print {
    display: none !important;
  }

  .sibling-concession-report {
    background: white !important;
    padding: 0 !important;
  }

  .report-page {
    box-shadow: none !important;
    padding: 1.2cm 0.8cm !important;
    max-width: 100% !important;
    width: 100% !important;
    margin: 0 !important;
  }

  .family-block {
    page-break-inside: avoid;
    break-inside: avoid;
  }

  .ledger-style-table {
    font-size: 8pt !important;

    th,
    td {
      padding: 5px 6px !important;
      border: 1px solid #000 !important;
      color: #000 !important;
    }

    thead th {
      background: #f1f2f6 !important;
      color: #000 !important;
      -webkit-print-color-adjust: exact;
      print-color-adjust: exact;
    }
  }

  .family-header {
    background: #f3f4f6 !important;
    border: 1px solid #000 !important;
    -webkit-print-color-adjust: exact;
    print-color-adjust: exact;
  }

  .grand-total-bar {
    background: #f3f4f6 !important;
    color: #000 !important;
    border: 2px solid #000 !important;
    -webkit-print-color-adjust: exact;
    print-color-adjust: exact;
  }
}
</style>
